"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent } from "react";
import { ArrowRight, BadgeDollarSign, BarChart3, BookOpen, Boxes, Brain, Building2, Calculator, ChevronDown, ChevronRight, Cloud, Code2, Coins, Columns2, Cpu, Database, FlaskConical, Gauge, Handshake, HeartPulse, Languages, MessagesSquare, Menu, Monitor, Moon, NotebookText, PencilLine, PiggyBank, Rocket, Scale, ScrollText, Search, SearchCheck, ShoppingCart, Sparkles, Sun, Trophy, User, Users, Waypoints, X } from "lucide-react";
import type { Locale } from "@/lib/i18n/config";
import { localeName, locales } from "@/lib/i18n/config";
import { availableLocales, canonicalPath, localePath } from "@/lib/i18n/routes";
import { LOCALE_COOKIE, LOCALE_COOKIE_MAX_AGE, preferredLocale } from "@/lib/i18n/negotiate";
import type { Dictionary } from "@/lib/i18n/es";
import type { MenuLink, MenuSection, NavEntry, NavItem, NavPanel } from "@/lib/site-menu";
import { THEME_KEY, THEMES_PUBLIC, type ThemePreference } from "@/lib/theme";
import { OPEN_SEARCH_EVENT } from "@/components/home/home-interactive";

/** The header's copy minus the server-only formatters (functions can't reach the client). */
type MenuCopy = Dictionary["menu"];
type Copy = { [K in keyof MenuCopy as MenuCopy[K] extends (...args: never[]) => unknown ? never : K]: MenuCopy[K] };
export type HeaderCopy = Copy;

const ICONS = { ai: Sparkles, cloud: Cloud, databases: Database, embeddings: Waypoints, storage: Boxes, queues: Cpu, guides: BookOpen, compare: Columns2, research: BarChart3, services: Handshake,
  best: Trophy, value: Scale, benchmarks: Gauge } as const;

function Logo() {
  /* eslint-disable @next/next/no-img-element */
  return <span className="cfh-logo">
    <img className="cfh-logo-light" src="/brand/codifly-logo.svg" alt="Codifly" width={480} height={120} />
    <img className="cfh-logo-dark" src="/brand/codifly-logo-dark.svg" alt="" aria-hidden="true" width={480} height={120} />
  </span>;
}

const subscribeTheme = (onChange: () => void) => {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme-preference"] });
  return () => observer.disconnect();
};

function ThemeToggle({ copy }: { copy: Copy }) {
  // The preference is written to <html> by the pre-paint theme script; read it from there.
  const stored = useSyncExternalStore(subscribeTheme, () => (document.documentElement.dataset.themePreference as ThemePreference) || "system", () => "system" as ThemePreference);
  const [chosen, setPref] = useState<ThemePreference | null>(null);
  const pref = chosen ?? stored;
  if (!THEMES_PUBLIC) return null;
  const next: Record<ThemePreference, ThemePreference> = { system: "light", light: "dark", dark: "system" };
  const label = { system: copy.themeSystem, light: copy.themeLight, dark: copy.themeDark }[pref];
  const Icon = pref === "dark" ? Moon : pref === "light" ? Sun : Monitor;
  const cycle = () => {
    const value = next[pref];
    setPref(value);
    try { localStorage.setItem(THEME_KEY, value); } catch { /* private mode: the choice lasts this page */ }
    const dark = value === "dark" || (value === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    document.documentElement.dataset.themePreference = value;
  };
  return <button type="button" className="ui-icon-btn" onClick={cycle} aria-label={`${copy.theme}: ${label}`} title={`${copy.theme}: ${label}`}><Icon aria-hidden="true" /></button>;
}

/** Remembers the visitor's language choice (switcher or hint); "/" then honours it before the browser's. */
function rememberLocale(locale: Locale) {
  try { document.cookie = `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`; } catch { /* Cookies disabled: the choice lasts this page. */ }
}

const HINT: Record<Locale, { text: string; go: string; stay: (name: string) => string; label: string }> = {
  es: { text: "Esta página está disponible en español.", go: "Ver en español", stay: name => `Seguir en ${name}`, label: "Sugerencia de idioma" },
  en: { text: "This page is available in English.", go: "View in English", stay: name => `Continue in ${name}`, label: "Language suggestion" },
  pt: { text: "Esta página está disponível em português.", go: "Ver em português", stay: name => `Continuar em ${name}`, label: "Sugestão de idioma" },
};

/** Suggests (never forces) the same page in the browser's language, in that language. Rendered only
 * on the client, so the HTML every crawler and the CDN see is identical; hidden once a choice exists. */
function LanguageHint({ locale }: { locale: Locale }) {
  const pathname = usePathname() ?? "/";
  const [target, setTarget] = useState<Locale | null>(null);
  useEffect(() => {
    if (document.cookie.split(";").some(c => c.trim().startsWith(`${LOCALE_COOKIE}=`))) return;
    const wanted = preferredLocale(navigator.languages?.length ? navigator.languages : [navigator.language]);
    // Deferred so the hint never takes part in hydration.
    if (wanted && wanted !== locale && availableLocales(pathname).includes(wanted)) { const id = setTimeout(() => setTarget(wanted), 0); return () => clearTimeout(id); }
  }, [locale, pathname]);
  if (!target) return null;
  const copy = HINT[target];
  const go = () => { rememberLocale(target); window.location.assign(`${localePath(target, pathname)}${window.location.search}${window.location.hash}`); };
  const stay = () => { rememberLocale(locale); setTarget(null); };
  return <div className="cfh-hint" role="region" aria-label={copy.label} lang={target}>
    <div className="ui-container cfh-hint-in">
      <Languages aria-hidden="true" /><span>{copy.text}</span>
      <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" onClick={go}>{copy.go}</button>
      <button type="button" className="cfh-hint-stay" onClick={stay}>{copy.stay(localeName[locale])}</button>
    </div>
  </div>;
}

/** Every language edition of the current page; query string and fragment travel along. */
function LanguageMenu({ locale, copy, compact = false }: { locale: Locale; copy: Copy; compact?: boolean }) {
  const pathname = usePathname() ?? "/";
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => { if (!ref.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);
  const keepState = (href: string, chosen: Locale) => (event: MouseEvent<HTMLAnchorElement>) => {
    rememberLocale(chosen);
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const { search, hash } = window.location;
    if (!search && !hash) return;
    event.preventDefault();
    window.location.assign(`${href}${search}${hash}`);
  };
  const options = locales.map(l => ({ locale: l, href: localePath(l, pathname) }));
  if (compact) return <div className="cfh-lang-list" role="list" aria-label={copy.language}>{options.map(o => <a role="listitem" key={o.locale} href={o.href} hrefLang={o.locale} lang={o.locale} aria-current={o.locale === locale ? "true" : undefined} onClick={keepState(o.href, o.locale)}>{localeName[o.locale]}</a>)}</div>;
  return <div className="cfh-lang" ref={ref}>
    <button type="button" className="cfh-lang-btn" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen(v => !v)} aria-label={copy.language}><Languages aria-hidden="true" /><span>{locale.toUpperCase()}</span></button>
    {open && <div className="cfh-lang-pop" role="menu">{options.map(o => <a role="menuitem" key={o.locale} href={o.href} hrefLang={o.locale} lang={o.locale} aria-current={o.locale === locale ? "true" : undefined} onClick={keepState(o.href, o.locale)}>{localeName[o.locale]}<span>{o.locale.toUpperCase()}</span></a>)}</div>}
  </div>;
}

const LINK_ICONS: Record<NonNullable<MenuLink["icon"]>, typeof Gauge> = { trophy: Trophy, cart: ShoppingCart, rocket: Rocket, dollar: BadgeDollarSign, heart: HeartPulse, chat: MessagesSquare, search: SearchCheck, gauge: Gauge, brain: Brain, code: Code2, context: ScrollText, scale: Scale, coins: Coins, calculator: Calculator,
  embeddings: Waypoints, cloud: Cloud, database: Database, storage: Boxes, queue: Cpu, versus: Columns2, book: BookOpen, flask: FlaskConical, building: Building2, method: NotebookText, team: Users, pencil: PencilLine, piggy: PiggyBank };
const hoverCapable = () => typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
/** Category and link icons in one lookup (a plain map, so no component is created during render). */
const NAV_ICONS: Record<NavItem["icon"], typeof Gauge> = { ...ICONS, ...LINK_ICONS };

function NavTile({ icon, tone, size = "md" }: { icon: NavItem["icon"]; tone?: string; size?: "sm" | "md" | "lg" }) {
  const Icon = NAV_ICONS[icon];
  return <span className={`cfh-tile cfh-tile-${size} cfh-tone-${tone ?? "slate"}`}><Icon aria-hidden="true" /></span>;
}

/** A tiled row: icon in its tone, label (with an optional «Nuevo» badge) and a figure or meta line. */
function NavRow({ item, onNavigate, size = "md" }: { item: NavItem; onNavigate: () => void; size?: "sm" | "md" | "lg" }) {
  const sub = item.figure ?? item.meta;
  return <Link prefetch={false} className={`cfh-row cfh-row-${size}`} href={item.href} onClick={onNavigate}>
    <NavTile icon={item.icon} tone={item.tone} size={size} />
    <span className="cfh-row-text">
      <span className="cfh-row-label">{item.label}{item.badge && <em className="cfh-badge">{item.badge}</em>}</span>
      {sub && <span className={item.figure ? "cfh-row-figure" : "cfh-row-meta"}>{sub}</span>}
    </span>
  </Link>;
}

/** Short menus (Compare, Benchmarks, Pricing, Guides): a floating card under their button. */
function ListPanel({ panel, close }: { panel: Extract<NavPanel, { kind: "list" }>; close: () => void }) {
  return <div className="cfh-drop">
    {panel.items.map(item => <NavRow key={item.href + item.label} item={item} onNavigate={close} size="lg" />)}
    {panel.footer && <Link prefetch={false} className="cfh-drop-foot" href={panel.footer.href} onClick={close}>{panel.footer.label} <ArrowRight aria-hidden="true" /></Link>}
  </div>;
}

/** Rankings (LLM Stats style): hovering or focusing a rail item swaps the columns on the right. */
function MegaPanel({ panel, close }: { panel: Extract<NavPanel, { kind: "mega" }>; close: () => void }) {
  const [active, setActive] = useState(0);
  const current = panel.rail[active] ?? panel.rail[0];
  return <div className="cfh-mega2">
    <div className="cfh-mega2-rail">{panel.rail.map((item, i) => <div key={item.href} className={i === active ? "cfh-mega2-lead" : undefined}
      onMouseEnter={() => { if (hoverCapable()) setActive(i); }} onFocus={() => setActive(i)}><NavRow item={item} onNavigate={close} size="lg" /></div>)}</div>
    <div className="cfh-mega2-cols" aria-live="polite">{current.columns.map(column => <div key={column.title} className="cfh-mega2-col">
      <h3 className="cfh-col-title"><span>{column.title}</span></h3>
      {column.items.map(item => <NavRow key={item.href + item.label} item={item} onNavigate={close} size="md" />)}
    </div>)}</div>
  </div>;
}

/** Models: categories on the left; search, key links and every product with its provider mark on the right. */
function ExplorerPanel({ panel, close, empty }: { panel: Extract<NavPanel, { kind: "explorer" }>; close: () => void; empty: string }) {
  const [tab, setTab] = useState(panel.tabs[0].id);
  const [query, setQuery] = useState("");
  const current = panel.tabs.find(t => t.id === tab) ?? panel.tabs[0];
  const needle = fold(query.trim());
  const products = current.products.filter(p => !needle || fold(`${p.label} ${p.provider}`).includes(needle));
  const select = (id: typeof tab) => { setTab(id); setQuery(""); };
  return <div className="cfh-explorer">
    <div className="cfh-explorer-tabs" role="tablist" aria-orientation="vertical">
      {panel.tabs.map(t => <button key={t.id} type="button" role="tab" id={`cfh-tab-${t.id}`} aria-selected={t.id === current.id} aria-controls="cfh-explorer-body"
        onMouseEnter={() => { if (hoverCapable()) select(t.id); }} onFocus={() => select(t.id)} onClick={() => select(t.id)}>{t.label}</button>)}
    </div>
    <div className="cfh-explorer-body" id="cfh-explorer-body" role="tabpanel" aria-labelledby={`cfh-tab-${current.id}`}>
      <label className="cfh-explorer-search"><Search aria-hidden="true" /><input value={query} onChange={e => setQuery(e.target.value)} placeholder={current.search} aria-label={current.search} /></label>
      <section className="cfh-explorer-section">
        <h3 className="cfh-explorer-title">{current.keyTitle}</h3>
        <div className="cfh-explorer-keys">{current.keyLinks.map(item => { const Icon = NAV_ICONS[item.icon]; return <Link prefetch={false} key={item.href + item.label} className="cfh-key" href={item.href} onClick={close}><Icon aria-hidden="true" /><span>{item.label}</span>{item.badge && <em className="cfh-badge">{item.badge}</em>}</Link>; })}</div>
      </section>
      <section className="cfh-explorer-section">
        <h3 className="cfh-explorer-title">{current.listTitle}</h3>
        <div className="cfh-explorer-grid">
          {products.map(p => <Link prefetch={false} key={p.href} className="cfh-product" href={p.href} onClick={close}>
            <span className={`ui-provider ui-provider-${p.brand}`} aria-hidden="true">{p.mark}</span>
            <span className="cfh-product-text"><span className="cfh-product-name">{p.label}</span><span className="cfh-row-meta">{p.provider}</span></span>
            {p.figure && <span className="cfh-row-figure">{p.figure}</span>}
          </Link>)}
          {!products.length && <p className="cfh-explorer-empty">{empty.replace("{query}", query)}</p>}
        </div>
      </section>
    </div>
  </div>;
}

/** Everything a nav entry links to, flattened for the mobile drawer. */
function drawerItems(panel: NavPanel): NavItem[] {
  if (panel.kind === "list") return [...panel.items, ...(panel.footer ? [panel.footer] : [])];
  if (panel.kind === "mega") return [...panel.rail, ...panel.rail[0].columns.flatMap(c => c.items)];
  return panel.tabs.map(t => ({ label: t.label, href: t.keyLinks[0].href, icon: t.id, tone: t.tone, meta: String(t.products.length) }));
}

type Hit = MenuLink & { group: string };
function flatten(menu: MenuSection[], copy: Copy): Hit[] {
  const seen = new Set<string>();
  const hits: Hit[] = [];
  const push = (link: MenuLink, group: string) => { if (seen.has(link.href + link.label)) return; seen.add(link.href + link.label); hits.push({ ...link, group }); };
  for (const section of menu) {
    if (section.group === "insights") {
      section.columns.flatMap(c => c.links).forEach(l => push(l, l.href.includes("/compare/") ? copy.palette.comparisons : l.href.includes("/articles/") ? copy.palette.guides : copy.palette.pages));
    } else if (section.group === "categories") {
      section.columns[0].links.forEach(l => push(l, copy.palette.products));
      section.columns[1].links.forEach(l => push(l, copy.palette.comparisons));
      section.guides?.links.forEach(l => push(l, copy.palette.guides));
      section.columns[2].links.forEach(l => push(l, copy.palette.pages));
    } else {
      section.columns.flatMap(c => c.links).forEach(l => push(l, section.id === "guides" ? copy.palette.guides : copy.palette.pages));
    }
  }
  return hits;
}
const fold = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

function Palette({ menu, copy, onClose }: { menu: MenuSection[]; copy: Copy; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const all = useMemo(() => flatten(menu, copy), [menu, copy]);
  const results = useMemo(() => {
    const terms = fold(query).split(/\s+/).filter(Boolean);
    if (!terms.length) return all.filter(h => h.group === copy.palette.products).slice(0, 8);
    return all.map(h => {
      const text = fold(`${h.label} ${h.meta ?? ""}`);
      if (!terms.every(t => text.includes(t))) return null;
      const title = fold(h.label);
      return { h, score: terms.reduce((s, t) => s + (title.startsWith(t) ? 3 : title.includes(` ${t}`) ? 2 : 1), 0) };
    }).filter((x): x is { h: Hit; score: number } => !!x).sort((a, b) => b.score - a.score).map(x => x.h).slice(0, 24);
  }, [query, all, copy]);
  useEffect(() => { input.current?.focus(); }, []);
  const groups = [copy.palette.products, copy.palette.comparisons, copy.palette.guides, copy.palette.pages];
  const ordered = groups.flatMap(g => results.filter(r => r.group === g));
  const go = (hit?: Hit) => { if (!hit) return; onClose(); window.location.assign(hit.href); };
  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelected(i => Math.min(i + 1, ordered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelected(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(ordered[selected]); }
  };
  let index = -1;
  return <div className="cfh-overlay ui-root" onPointerDown={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="cfh-palette" role="dialog" aria-modal="true" aria-label={copy.palette.label}>
      <div className="cfh-palette-input"><Search aria-hidden="true" /><input ref={input} value={query} onChange={e => { setQuery(e.target.value); setSelected(0); }} onKeyDown={onKey} placeholder={copy.palette.placeholder} aria-label={copy.palette.label} role="combobox" aria-expanded="true" aria-controls="cfh-palette-list" aria-activedescendant={ordered[selected] ? `cfh-hit-${selected}` : undefined} /><kbd className="ui-kbd">esc</kbd></div>
      <div className="cfh-palette-list" id="cfh-palette-list" role="listbox">
        {!ordered.length && <div className="cfh-palette-empty"><b>{copy.palette.empty.replace("{query}", query)}</b><span>{copy.palette.emptyHint}</span></div>}
        {groups.map(group => {
          const items = ordered.filter(r => r.group === group);
          if (!items.length) return null;
          return <div key={group}><div className="cfh-palette-group">{query ? group : copy.palette.popular}</div>{items.map(hit => { index += 1; const i = index; return <a key={hit.href + hit.label} id={`cfh-hit-${i}`} role="option" aria-selected={i === selected} className="cfh-hit" href={hit.href} onMouseEnter={() => setSelected(i)} onClick={e => { e.preventDefault(); go(hit); }}><span className="cfh-hit-label">{hit.label}</span>{hit.figure ? <span className="cfh-link-figure">{hit.figure}</span> : hit.meta ? <span className="cfh-link-meta">{hit.meta}</span> : <ChevronRight aria-hidden="true" />}</a>; })}</div>;
        })}
      </div>
      <div className="cfh-palette-foot"><span><kbd className="ui-kbd">↑</kbd><kbd className="ui-kbd">↓</kbd> {copy.palette.navigate}</span><span><kbd className="ui-kbd">↵</kbd> {copy.palette.open}</span><span><kbd className="ui-kbd">esc</kbd> {copy.palette.close}</span></div>
    </div>
  </div>;
}

export function SiteHeaderBar({ locale, menu, nav, copy }: { locale: Locale; menu: MenuSection[]; nav: NavEntry[]; copy: Copy }) {
  const [open, setOpen] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const openTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hoverOpenedAt = useRef(0);
  const pathname = usePathname() ?? "/";
  // Internal Spanish form of the URL (/rankings/ia → /leaderboards/ai), whatever the language.
  const here = canonicalPath(pathname);
  const href = (path: string) => localePath(locale, path);
  const close = useCallback(() => { setOpen(null); setDrawer(false); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = /^(input|textarea|select)$/i.test((e.target as HTMLElement)?.tagName ?? "");
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(null); setPalette(true); }
      else if (e.key === "/" && !typing) { e.preventDefault(); setOpen(null); setPalette(true); }
      else if (e.key === "Escape") { setPalette(false); setOpen(null); setDrawer(false); }
    };
    // Any "search" affordance on the page (e.g. the home hero) opens the same palette.
    const openSearch = () => { setOpen(null); setPalette(true); };
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_SEARCH_EVENT, openSearch);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener(OPEN_SEARCH_EVENT, openSearch); };
  }, []);
  useEffect(() => { document.body.style.overflow = drawer || palette ? "hidden" : ""; }, [drawer, palette]);
  // Over a deep hero (#cf-hero, the home page) the bar turns to dark glass until the hero scrolls away.
  const [overDeep, setOverDeep] = useState(false);
  useEffect(() => {
    const hero = document.getElementById("cf-hero");
    if (!hero) return;
    const update = () => setOverDeep(hero.getBoundingClientRect().bottom > 64);
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { window.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, []);

  const current = (entry: NavEntry) => entry.match.some(prefix => here === prefix || here.startsWith(`${prefix}/`));
  const opened = nav.find(entry => entry.id === open);
  const wide = opened?.panel && opened.panel.kind !== "list" ? opened.panel : null;

  return <header className={`cfh ui-root${overDeep && !open ? " is-deep" : ""}`} onMouseLeave={() => { clearTimeout(openTimer.current); closeTimer.current = setTimeout(() => setOpen(null), 180); }} onMouseEnter={() => clearTimeout(closeTimer.current)}>
    <LanguageHint locale={locale} />
    <div className="ui-container cfh-bar">
      <Link prefetch={false} href={href("/")} className="cfh-home" aria-label="Codifly" onClick={close}><Logo /></Link>
      <nav className="cfh-nav" aria-label={copy.main}>
        {nav.map(entry => entry.panel
          ? <div key={entry.id} className="cfh-nav-item">
            <button type="button" className={`cfh-nav-btn${current(entry) ? " is-current" : ""}`} aria-expanded={open === entry.id} aria-haspopup="true"
              onClick={() => {
                clearTimeout(openTimer.current);
                // A click that lands right after hover opened this entry confirms it instead of closing it.
                if (open === entry.id && Date.now() - hoverOpenedAt.current > 600) setOpen(null); else setOpen(entry.id);
              }}
              onMouseEnter={() => { if (!hoverCapable()) return; clearTimeout(openTimer.current); openTimer.current = setTimeout(() => { hoverOpenedAt.current = Date.now(); setOpen(entry.id); }, open ? 0 : 90); }}>
              {entry.label}
            </button>
            {open === entry.id && entry.panel.kind === "list" && <ListPanel panel={entry.panel} close={close} />}
          </div>
          : <Link key={entry.id} prefetch={false} className={`cfh-nav-btn cfh-nav-link${current(entry) ? " is-current" : ""}`} href={entry.href!} onClick={close} onMouseEnter={() => setOpen(null)}>{entry.label}</Link>)}
      </nav>
      <button type="button" className="cfh-search" onClick={() => { setOpen(null); setPalette(true); }} aria-label={copy.search}>
        <Search aria-hidden="true" /><span className="cfh-search-text">{copy.search}</span><kbd className="ui-kbd">⌘K</kbd>
      </button>
      <div className="cfh-actions">
        <Link prefetch={false} className="ui-icon-btn cfh-hide-sm" href={href("/account")} aria-label={copy.account} title={copy.account}><User aria-hidden="true" /></Link>
        <ThemeToggle copy={copy} />
        <span className="cfh-hide-sm"><LanguageMenu locale={locale} copy={copy} /></span>
        <Link prefetch={false} className="ui-btn ui-btn-brand cfh-subscribe" href={href("/#newsletter")}>{copy.subscribe}</Link>
        <button type="button" className="ui-icon-btn cfh-burger" onClick={() => setDrawer(true)} aria-label={copy.openMenu} aria-expanded={drawer}><Menu aria-hidden="true" /></button>
      </div>
    </div>

    {wide && <div id="cfh-mega" className="cfh-mega is-open">
      <div className="ui-container cfh-mega-wrap">
        {wide.kind === "mega" ? <MegaPanel panel={wide} close={close} /> : <ExplorerPanel key={opened!.id} panel={wide} close={close} empty={copy.palette.empty} />}
      </div>
      <div className="ui-container cfh-mega-foot">
        <Link prefetch={false} href={href("/leaderboards/ai")} onClick={close}>{copy.allTools}</Link>
        <Link prefetch={false} href={href("/compare")} onClick={close}>{copy.allComparisons}</Link>
        <Link prefetch={false} href={href("/research")} onClick={close}>{copy.allGuides}</Link>
        <Link prefetch={false} href={href("/methodology")} onClick={close}>{copy.methodology}</Link>
        <span className="cfh-mega-hint"><kbd className="ui-kbd">⌘K</kbd> {copy.searchHint}</span>
      </div>
    </div>}

    {drawer && createPortal(<div className="cfh-drawer ui-root" role="dialog" aria-modal="true" aria-label={copy.main}>
      <div className="cfh-drawer-head"><Link prefetch={false} href={href("/")} onClick={close} aria-label="Codifly"><Logo /></Link><button type="button" className="ui-icon-btn" onClick={() => setDrawer(false)} aria-label={copy.closeMenu}><X aria-hidden="true" /></button></div>
      <button type="button" className="cfh-search cfh-search-block" onClick={() => { setDrawer(false); setPalette(true); }}><Search aria-hidden="true" /><span className="cfh-search-text">{copy.search}</span></button>
      <div className="cfh-drawer-group">
        {nav.map(entry => entry.panel
          ? <details key={entry.id} className="cfh-drawer-section">
            <summary><span>{entry.label}</span><ChevronDown aria-hidden="true" className="cfh-drawer-chevron" /></summary>
            <div className="cfh-drawer-links">{drawerItems(entry.panel).map(item => <NavRow key={item.href + item.label} item={item} onNavigate={close} size="sm" />)}</div>
          </details>
          : <Link key={entry.id} prefetch={false} className="cfh-drawer-plain" href={entry.href!} onClick={close}>{entry.label}</Link>)}
      </div>
      <div className="cfh-drawer-group"><div className="cfh-rail-title">{copy.language}</div><LanguageMenu locale={locale} copy={copy} compact /></div>
      <div className="cfh-drawer-cta"><Link prefetch={false} className="ui-btn ui-btn-brand ui-btn-lg" href={href("/#newsletter")} onClick={close}>{copy.subscribe}</Link><Link prefetch={false} className="ui-btn ui-btn-outline ui-btn-lg" href={href("/advisory")} onClick={close}>{copy.advisory}</Link></div>
    </div>, document.body)}

    {/* Portals: the bar's backdrop-filter would otherwise contain these fixed layers. */}
    {palette && createPortal(<Palette menu={menu} copy={copy} onClose={() => setPalette(false)} />, document.body)}
  </header>;
}
